import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { AuditType, IAuditConfig } from '../widget-audits/data/audits';
import { AuditFilterOptions, AuditStatusConfig, getAuditFilterDefault } from './audit-filter.service';
import { Subscription } from 'rxjs';

export interface AuditFilterCacheState {
  [auditType: string]: {
    filterControl: FormGroup;
    lastUpdated: number;
    auditCount: number;
  };
}

@Injectable()
export class AuditCacheService implements OnDestroy {
  AuditFilterOptions = AuditFilterOptions;
  AuditStatusConfig = AuditStatusConfig;

  private _filterCache: AuditFilterCacheState = {};
  private _auditTypeExpandedStates: { [auditType: string]: boolean } = {};
  private subscription = new Subscription();

  /**
   * In-memory auditsRan cache for the session
   */
  private _auditsRan: { [key: string]: boolean } = {};

  /**
   * Get the current auditsRan state (in-memory, session only)
   */
  get auditsRan(): { [key: string]: boolean } {
    return this._auditsRan;
  }

  /**
   * Set the auditsRan state (in-memory, session only)
   */
  set auditsRan(value: { [key: string]: boolean }) {
    this._auditsRan = value || {};
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Get or create filter control for a specific audit type
   */
  getFilterControl(auditType: AuditType): FormGroup {
    if (!this._filterCache[auditType]) {
      this._filterCache[auditType] = {
        filterControl: new FormGroup({
          [AuditFilterOptions.INFORMATIVE]: new FormControl(getAuditFilterDefault(AuditFilterOptions.INFORMATIVE)),
          [AuditFilterOptions.PASSED]: new FormControl(getAuditFilterDefault(AuditFilterOptions.PASSED)),
          [AuditFilterOptions.ISSUES]: new FormControl(getAuditFilterDefault(AuditFilterOptions.ISSUES)),
          [AuditFilterOptions.NOT_RAN]: new FormControl(getAuditFilterDefault(AuditFilterOptions.NOT_RAN)),
        }),
        lastUpdated: Date.now(),
        auditCount: 0
      };
    }
    return this._filterCache[auditType].filterControl;
  }

  /**
   * Update audit count for a specific audit type
   */
  updateAuditCount(auditType: AuditType, count: number): void {
    if (this._filterCache[auditType]) {
      this._filterCache[auditType].auditCount = count;
      this._filterCache[auditType].lastUpdated = Date.now();
    }
  }

  /**
   * Get expanded state for an audit type
   */
  getExpandedState(auditType: AuditType): boolean {
    return this._auditTypeExpandedStates[auditType] ?? false;
  }

  /**
   * Set expanded state for an audit type
   */
  setExpandedState(auditType: AuditType, expanded: boolean): void {
    this._auditTypeExpandedStates[auditType] = expanded;
  }

  /**
   * Reset filters for a specific audit type
   */
  resetFiltersForType(auditType: AuditType): void {
    if (this._filterCache[auditType]) {
      const filterControl = this._filterCache[auditType].filterControl;
      Object.keys(AuditStatusConfig).forEach(option => {
        filterControl.get(option)?.setValue(getAuditFilterDefault(option as AuditFilterOptions));
      });
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { totalTypes: number; totalAudits: number; lastUpdated: number } {
    const totalTypes = Object.keys(this._filterCache).length;
    const totalAudits = Object.values(this._filterCache).reduce((sum, cache) => sum + cache.auditCount, 0);
    const lastUpdated = Math.max(...Object.values(this._filterCache).map(cache => cache.lastUpdated), 0);
    
    return { totalTypes, totalAudits, lastUpdated };
  }

  /**
   * Clear the entire cache
   */
  clearCache(): void {
    this._filterCache = {};
    this._auditTypeExpandedStates = {};
  }

  /**
   * Get all cached audit types
   */
  getCachedAuditTypes(): AuditType[] {
    return Object.keys(this._filterCache) as AuditType[];
  }

  /**
   * Check if a filter option is active for a specific audit type
   */
  isFilterActive(auditType: AuditType, option: AuditFilterOptions): boolean {
    const filterControl = this.getFilterControl(auditType);
    return !!filterControl.get(option)?.value;
  }
} 